export const ENDPOINTS = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    updateProfile: '/auth/profile',
    changePassword: '/auth/change-password',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
  },
  products: {
    list: '/products',
    create: '/products',
    bulk: '/products/bulk',
    detail: (id: string | number) => `/products/${id}`,
    update: (id: string | number) => `/products/${id}`,
    remove: (id: string | number) => `/products/${id}`,
    byBarcode: (barcode: string) => `/products/barcode/${barcode}`,
    stats: '/products/stats',
    inventoryStats: '/products/inventory-stats',
    lowStock: '/products/low-stock',
    uploadImage: (id: string | number) => `/products/${id}/image`,
    uploadGalleryImage: (id: string | number) => `/products/${id}/images`,
    removeGalleryImage: (id: string | number) => `/products/${id}/images`,
    reviews: (id: string | number) => `/products/${id}/reviews`,
  },
  reviews: {
    list: '/reviews',
    detail: (id: string | number) => `/reviews/${id}`,
    remove: (id: string | number) => `/reviews/${id}`,
  },
  categories: {
    list: '/categories',
    create: '/categories',
    bulk: '/categories/bulk',
    detail: (id: string | number) => `/categories/${id}`,
    update: (id: string | number) => `/categories/${id}`,
  },
  customers: {
    list: '/customers',
    create: '/customers',
    bulk: '/customers/bulk',
    detail: (id: string | number) => `/customers/${id}`,
    remove: (id: string | number) => `/customers/${id}`,
    purchases: (id: string | number) => `/customers/${id}/purchases`,
  },
  expenses: {
    list: '/expenses',
    byReference: '/expenses/by-reference',
    create: '/expenses',
    bulk: '/expenses/bulk',
    detail: (id: string | number) => `/expenses/${id}`,
    update: (id: string | number) => `/expenses/${id}`,
    remove: (id: string | number) => `/expenses/${id}`,
  },
  expenseCategories: {
    list: '/expense-categories',
    create: '/expense-categories',
    detail: (id: string | number) => `/expense-categories/${id}`,
    update: (id: string | number) => `/expense-categories/${id}`,
    remove: (id: string | number) => `/expense-categories/${id}`,
  },
  suppliers: {
    list: '/suppliers',
    create: '/suppliers',
    bulk: '/suppliers/bulk',
    detail: (id: string | number) => `/suppliers/${id}`,
  },
  inventory: {
    movements: '/inventory/movements',
    consumptionByProduct: '/inventory/consumption/by-product',
    stockIn: '/inventory/stock-in',
    stockOut: '/inventory/stock-out',
    adjust: '/inventory/adjust',
    history: '/inventory/history',
  },
  purchaseOrders: {
    list: '/purchase-orders',
    stats: '/purchase-orders/stats',
    create: '/purchase-orders',
    bulk: '/purchase-orders/bulk',
    detail: (id: string | number) => `/purchase-orders/${id}`,
    receive: (id: string | number) => `/purchase-orders/${id}/receive`,
    pay: (id: string | number) => `/purchase-orders/${id}/pay`,
  },
  sales: {
    list: '/sales',
    stats: '/sales/stats',
    receivables: '/sales/receivables',
    rentals: '/sales/rentals',
    hireItems: '/sales/rentals/hire-items',
    create: '/sales',
    bulk: '/sales/bulk',
    detail: (id: string | number) => `/sales/${id}`,
    payment: (id: string | number) => `/sales/${id}/payment`,
    email: (id: string | number) => `/sales/${id}/email`,
    refund: (id: string | number) => `/sales/${id}/refund`,
    return: (id: string | number) => `/sales/${id}/return`,
    returnItem: (id: string | number) => `/sales/${id}/return-item`,
    void: (id: string | number) => `/sales/${id}/void`,
  },
  quotes: {
    list: '/quotes',
    create: '/quotes',
    detail: (id: string | number) => `/quotes/${id}`,
    update: (id: string | number) => `/quotes/${id}`,
    remove: (id: string | number) => `/quotes/${id}`,
    status: (id: string | number) => `/quotes/${id}/status`,
    convert: (id: string | number) => `/quotes/${id}/convert`,
    email: (id: string | number) => `/quotes/${id}/email`,
  },
  orders: {
    list: '/orders',
    detail: (id: string | number) => `/orders/${id}`,
    remove: (id: string | number) => `/orders/${id}`,
    markPaid: (id: string | number) => `/orders/${id}/mark-paid`,
    convert: (id: string | number) => `/orders/${id}/convert`,
  },
  employees: {
    list: '/employees',
    create: '/employees',
    bulk: '/employees/bulk',
    detail: (id: string | number) => `/employees/${id}`,
    update: (id: string | number) => `/employees/${id}`,
    remove: (id: string | number) => `/employees/${id}`,
    adjustments: (id: string | number) => `/employees/${id}/adjustments`,
    adjustment: (id: string | number, adjId: string | number) =>
      `/employees/${id}/adjustments/${adjId}`,
  },
  paymentCredentialsSelf: '/payment-credentials',
  users: {
    list: '/users',
    create: '/users',
    bulk: '/users/bulk',
    detail: (id: string | number) => `/users/${id}`,
    remove: (id: string | number) => `/users/${id}`,
    resetPassword: (id: string | number) => `/users/${id}/reset-password`,
  },
  roles: {
    list: '/roles',
    create: '/roles',
    detail: (slug: string) => `/roles/${slug}`,
  },
  reports: {
    salesDaily: '/reports/sales/daily',
    salesMonthly: '/reports/sales/monthly',
    profit: '/reports/profit',
    inventory: '/reports/inventory',
    topProducts: '/reports/top-products',
    sales: '/reports/sales',
    purchases: '/reports/purchases',
    expenses: '/reports/expenses',
    series: '/reports/series',
  },
  dashboard: {
    summary: '/dashboard/summary',
  },
  settings: {
    get: '/settings',
    update: '/settings',
    uploadLogo: '/settings/logo',
  },
  auditLogs: {
    list: '/audit-logs',
  },
  notifications: {
    list: '/notifications',
    markRead: (id: string | number) => `/notifications/${id}/read`,
    markAllRead: '/notifications/read-all',
  },
  mpesa: {
    initiate: '/payments/mpesa/initiate',
    status: (id: string | number) => `/payments/mpesa/status/${id}`,
  },
  content: {
    list: (type: string) => `/content?type=${encodeURIComponent(type)}`,
    create: '/content',
    update: (id: string | number) => `/content/${id}`,
    uploadImage: (id: string | number) => `/content/${id}/image`,
    uploadGalleryImage: (id: string | number) => `/content/${id}/images`,
    removeGalleryImage: (id: string | number) => `/content/${id}/images`,
    remove: (id: string | number) => `/content/${id}`,
  },
} as const;
